import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { ChangePasswordForm } from "@/components/ChangePassword";
import { AuthProvider, useAuth } from "@/lib/auth";
import Builder from "@/pages/Builder";
import History from "@/pages/History";
import Dashboard from "@/pages/Dashboard";
import FollowUps from "@/pages/FollowUps";
import FeeMaster from "@/pages/FeeMaster";
import Settings from "@/pages/Settings";
import ReceiptView from "@/pages/ReceiptView";
import Staff from "@/pages/Staff";
import Team from "@/pages/Team";
import Login from "@/pages/Login";
import Payments from "@/pages/Payments";
import ResetPassword from "@/pages/ResetPassword";

const AdminOnly = ({ children }) => {
  const { isAdmin } = useAuth();
  if (isAdmin) return children;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900" data-testid="admin-only-notice">
      <ShieldAlert className="h-5 w-5" /> This page is available to admins only.
    </div>
  );
};

const ForcedPw = () => {
  const { user, setUser, logout } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="fade-up w-full max-w-sm space-y-5" data-testid="forced-password-screen">
        <img src="/logo.png" alt="Indo Scots Global School" className="h-12 w-auto" />
        <h2 className="font-display text-2xl font-semibold text-plum-950">Welcome, {user.name}</h2>
        <ChangePasswordForm forced onDone={() => setUser({ ...user, must_change_password: false })} />
        <button onClick={logout} className="text-xs text-slate-500 hover:underline" data-testid="forced-logout-btn">Sign out</button>
      </div>
    </div>
  );
};

const Gate = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (pathname === "/reset-password") return <ResetPassword />;
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!user) return <Login />;
  if (user.must_change_password) return <ForcedPw />;
  if (user.role === "accountant") return (
    <AppShell>
      <Routes>
        <Route path="/receipts/:id" element={<ReceiptView />} />
        <Route path="*" element={<Payments />} />
      </Routes>
    </AppShell>
  );
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Builder />} />
        <Route path="/history" element={<History />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/follow-ups" element={<FollowUps />} />
        <Route path="/receipts/:id" element={<ReceiptView />} />
        <Route path="/fee-master" element={<AdminOnly><FeeMaster /></AdminOnly>} />
        <Route path="/settings" element={<AdminOnly><Settings /></AdminOnly>} />
        <Route path="/team" element={<AdminOnly><Team /></AdminOnly>} />
        <Route path="/staff" element={<AdminOnly><Staff /></AdminOnly>} />
      </Routes>
    </AppShell>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
      <Toaster position="bottom-right" richColors />
    </BrowserRouter>
  );
}

export default App;
